/**
 * Data-access hooks for the admin pages. Components call THESE, never useQuery /
 * useMutation against the API directly (andavo-dev convention). Each wraps a
 * factory in ./admin-queries.ts; all traffic flows through the vendored `api`
 * wrapper → the shared auth resolver seam.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminMutations, adminQueries } from './admin-queries';
import { uploadKbPdf } from './kb-upload';
import type { ImprovementStatus, AccessRequestStatus } from '../types';

export function useOpsDashboard() {
  return useQuery(adminQueries.opsDashboard());
}

export function useSystemInfo() {
  return useQuery(adminQueries.systemInfo());
}

export function useFeatureFlags() {
  return useQuery(adminQueries.featureFlags());
}

export function useCronAudit(job: string | null, limit: number) {
  return useQuery(adminQueries.cronAudit(job, limit));
}

export function useLlmBudget() {
  return useQuery(adminQueries.llmBudget());
}

export function useKbStats() {
  return useQuery(adminQueries.kbStats());
}

export function useSessionView(user: string, windowMinutes: number) {
  return useQuery(adminQueries.sessionView(user, windowMinutes));
}

export function useStatusPage() {
  return useQuery(adminQueries.statusPage());
}

export function useToggleFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    ...adminMutations.toggleFlag(),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: adminQueries.featureFlags().queryKey,
      });
    },
  });
}

export function useGrade() {
  return useMutation(adminMutations.grade());
}

export function useUploadKbPdf() {
  return useMutation({ mutationFn: uploadKbPdf });
}

export function useCreateKbPr() {
  return useMutation(adminMutations.kbPr());
}

// ── Access Requests + User Management (legacy admin.js parity) ──────────────

export function useAccessRequests(status: AccessRequestStatus | '') {
  return useQuery(adminQueries.accessRequests(status));
}

export function useAdminUsers() {
  return useQuery(adminQueries.adminUsers());
}

/**
 * Admin-only EXACT-email lookup for a single user — including a normally-hidden
 * system/test account (the default roster excludes those, #1377). Drives the
 * "find a specific account" affordance on User Management. Disabled until an
 * email is entered (enabled: Boolean(email)).
 */
export function useAdminUserByEmail(email: string) {
  return useQuery(adminQueries.adminUserByEmail(email));
}

/**
 * Approve / deny a single access request. On settle we invalidate EVERY
 * access-requests list (all status filters) so the card disappears from the
 * pending view and the counts/badges re-derive — no manual refetch needed.
 * The page's "Approve All" orchestrates this same mutation sequentially.
 */
export function useImprovements(status: ImprovementStatus | '') {
  return useQuery(adminQueries.improvements(status));
}

/**
 * Close / reopen an improvement suggestion. Invalidate every improvements
 * list on settle so cards move between the Pending/Closed filters.
 */
export function useImprovementAction() {
  const queryClient = useQueryClient();
  return useMutation({
    ...adminMutations.improvementAction(),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'improvements'],
      });
    },
  });
}

export function useAccessRequestAction() {
  const queryClient = useQueryClient();
  return useMutation({
    ...adminMutations.accessRequestAction(),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'access-requests'],
      });
    },
  });
}

/**
 * Grant / revoke admin. On settle we invalidate the user list (the badge +
 * action button must reflect the new role) AND the admin-me gate (if an admin
 * just revoked their OWN admin, the fail-closed guard must re-evaluate on the
 * next navigation — staleTime on /api/admin/me is short, but invalidating makes
 * it immediate).
 */
export function useSetUserRole() {
  const queryClient = useQueryClient();
  return useMutation({
    ...adminMutations.setUserRole(),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'me'] });
    },
  });
}

/**
 * Invite a person with their access already set. Invalidates the roster so the new
 * person appears (they arrive as a pending invite), and the admin-me gate in case
 * the invite granted platform admin.
 */
export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    ...adminMutations.inviteUser(),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'me'] });
    },
  });
}

/**
 * Unified role-SET write (the role-primary editor). Replaces the user's whole
 * stored-role set. Same invalidation contract as useSetUserRole: re-fetch the
 * roster (badge + role-set must reflect the new state) AND the admin-me gate (if
 * an admin just dropped their OWN 'admin' role, the fail-closed guard must
 * re-evaluate on the next navigation).
 */
export function useSetUserRoles() {
  const queryClient = useQueryClient();
  return useMutation({
    ...adminMutations.setUserRoles(),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'me'] });
    },
  });
}
